import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import type { CompetingPagesPayload } from '@taptimize/shared/dist/review/cannibalization-card';

/**
 * Which pages of the site compete for the same searches, for the review surface's Cannibalization
 * card (`GET /urls/competing-pages`; rules in shared/src/review/cannibalization-card.ts). The
 * server-side replay reads the same payload (`review-page-sources.ts`).
 *
 * Cached per domain, as `OwnSiteUrlsService` is: every page on the belt reads the same pairs, and
 * the verdict on one pair can depend on another pair of the same page.
 */
@Injectable({ providedIn: 'root' })
export class CompetingPagesService {
  private readonly API_URL = environment.apiUrl + '/v1';
  private readonly cache = new Map<string, Observable<CompetingPagesPayload | null>>();

  constructor(private http: HttpClient) {}

  /** The domain's payload, or null when the read failed. A failure is not cached. */
  getForDomain(domain: string): Observable<CompetingPagesPayload | null> {
    const key = (domain || '').trim().toLowerCase();
    const cached = this.cache.get(key);
    if (cached) return cached;

    const request = this.http
      .get<{ success: boolean; data: CompetingPagesPayload | null }>(`${this.API_URL}/urls/competing-pages`, {
        params: { domain: key }
      })
      .pipe(
        map((res) => res?.data ?? null),
        catchError(() => of(null)),
        tap((payload) => {
          if (!payload) this.cache.delete(key);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.cache.set(key, request);
    return request;
  }
}
